import { useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { getOpportunities } from "../../../apis/opportunities";
import { selectActiveNiche, readActiveNicheFromStorage } from "../../../store/nicheSlice";
import { normalizeOpportunitiesResponse } from "../../../utils/normalizeOpportunity";
import { useCursorPaginatedList, mergeById } from "../../../hooks/useCursorPaginatedList";
import {
    MAX_OPPORTUNITIES,
    isOpportunitySelected,
    toggleOpportunitySelection,
} from "../../../utils/opportunitySelection";
import ListScrollFooter from "../../shared/ListScrollFooter";
import OpportunityPickerItem from "./OpportunityPickerItem";
import "bootstrap-icons/font/bootstrap-icons.css";

function OpportunityPickerModal({ selected = [], onChange, onClose }) {
    const reduxNiche = useSelector(selectActiveNiche);
    const activeNiche = reduxNiche || readActiveNicheFromStorage();

    const fetchOpportunities = useCallback(
        (params) =>
            getOpportunities({
                ...params,
                ...(activeNiche ? { niche: activeNiche } : {}),
            }),
        [activeNiche]
    );

    const {
        items: opportunities,
        hasMore,
        loading,
        loadingMore,
        error,
        loadMoreRef,
    } = useCursorPaginatedList({
        fetchFn: fetchOpportunities,
        normalizeResponse: normalizeOpportunitiesResponse,
        mergeItems: mergeById,
    });

    const selectedCount = selected.length;
    const isMaxReached = selectedCount >= MAX_OPPORTUNITIES;

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === "Escape") onClose();
        };

        document.body.style.overflow = "hidden";
        window.addEventListener("keydown", handleKeyDown);

        return () => {
            document.body.style.overflow = "";
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [onClose]);

    const handleToggle = (opportunity) => {
        onChange(toggleOpportunitySelection(selected, opportunity));
    };

    return createPortal(
        <div
            className="fixed inset-0 z-[200] flex items-end justify-center bg-slate-900/50 p-0 sm:items-center sm:p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="opportunity-picker-title"
            onClick={onClose}
        >
            <div
                className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-2xl bg-white shadow-xl sm:rounded-2xl"
                onClick={(event) => event.stopPropagation()}
            >
                <div className="flex shrink-0 items-center justify-between border-b border-slate-200 px-4 py-4 md:px-6">
                    <div>
                        <h2
                            id="opportunity-picker-title"
                            className="mb-0 flex items-center gap-2 text-lg font-semibold text-slate-900 md:text-xl"
                        >
                            <i className="bi bi-fire text-orange-500" aria-hidden />
                            Choose trends
                        </h2>
                        <p className="mb-0 mt-1 text-sm text-slate-500">
                            Pick up to {MAX_OPPORTUNITIES} — {selectedCount}/{MAX_OPPORTUNITIES} selected
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
                        aria-label="Close"
                    >
                        <i className="bi bi-x-lg" aria-hidden />
                    </button>
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto px-2 py-4 md:px-6">
                    {error ? (
                        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">
                            <p className="mb-0 font-semibold">Could not load trends</p>
                            <p className="mt-1 text-sm">{error}</p>
                        </div>
                    ) : loading ? (
                        <div className="flex min-h-[12rem] items-center justify-center">
                            <div className="text-center">
                                <span
                                    className="spinner-border spinner-border-sm text-slate-700"
                                    role="status"
                                    aria-hidden
                                />
                                <p className="mt-3 text-sm text-slate-600">Finding trends…</p>
                            </div>
                        </div>
                    ) : opportunities.length === 0 ? (
                        <p className="text-center text-sm text-slate-500">
                            No trends right now — check back soon.
                        </p>
                    ) : (
                        <ul className="mb-0 space-y-3 p-0">
                            {opportunities.map((item, index) => {
                                const id = item.id ?? index;
                                const isSelected = isOpportunitySelected(selected, item.id);

                                return (
                                    <li key={id} className="list-none">
                                        <OpportunityPickerItem
                                            opportunity={item}
                                            isSelected={isSelected}
                                            selectDisabled={isMaxReached && !isSelected}
                                            onToggleSelect={handleToggle}
                                        />
                                    </li>
                                );
                            })}
                        </ul>
                    )}

                    <ListScrollFooter
                        loadMoreRef={loadMoreRef}
                        loadingMore={loadingMore}
                        showAllShown={
                            !loading && !loadingMore && opportunities.length > 0 && !hasMore
                        }
                    />
                </div>

                <div className="shrink-0 border-t border-slate-200 px-4 py-3 md:px-6">
                    <button
                        type="button"
                        onClick={onClose}
                        className="w-full rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800"
                    >
                        Done
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}

export default OpportunityPickerModal;
